import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import React from "react";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormLabel,
  Input,
} from "@/components";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import type {
  TProductJourneyConfigPayload,
  TProductJourneyField,
  TProductJourneyStep,
} from "../journey-config.types";
import {
  filterPresetsByProductType,
  presetToField,
} from "../journey-field-presets";
import {
  createProductJourneyConfig,
  getProductJourneyConfigByProductId,
  updateProductJourneyConfig,
} from "../product-journey-configs.api";
import { productJourneyConfigPayloadSchema } from "../schemas/journey-config.schema";
import { JourneyFieldEditor } from "./journey-field-editor";

const EMPTY: TProductJourneyConfigPayload = {
  schemaVersion: 1,
  fields: [],
  steps: [],
};

function normalizeJourneyPayload(
  draft: TProductJourneyConfigPayload,
): TProductJourneyConfigPayload {
  const { schemaVersion, fields } = draft;
  let steps = [...draft.steps]
    .filter((s) => s.fieldKeys.length > 0)
    .sort((a, b) => a.order - b.order);

  const allKeys = fields.map((f) => f.key);
  const inSteps = new Set(steps.flatMap((s) => s.fieldKeys));
  const orphans = allKeys.filter((k) => !inSteps.has(k));

  if (orphans.length > 0) {
    if (steps.length === 0) {
      steps = [
        {
          id: crypto.randomUUID(),
          order: 0,
          title: undefined,
          fieldKeys: [...orphans],
        },
      ];
    } else {
      const first = steps[0];
      steps[0] = {
        ...first,
        fieldKeys: [...new Set([...first.fieldKeys, ...orphans])],
      };
    }
  }

  if (fields.length > 0 && steps.length === 0) {
    steps = [
      {
        id: crypto.randomUUID(),
        order: 0,
        title: undefined,
        fieldKeys: [...allKeys],
      },
    ];
  }

  steps = steps.map((s, idx) => ({ ...s, order: idx }));

  return { schemaVersion, fields, steps };
}

const SNAKE_CASE_KEY = /^[a-z][a-z0-9_]*$/;

function appendFieldToPayload(
  prev: TProductJourneyConfigPayload,
  field: TProductJourneyField,
  defaultStepTitle: string,
): TProductJourneyConfigPayload {
  const fields = [...prev.fields, field];
  let steps = [...prev.steps];
  if (steps.length === 0) {
    steps = [
      {
        id: crypto.randomUUID(),
        order: 0,
        title: defaultStepTitle,
        fieldKeys: [field.key],
      },
    ];
  } else {
    const last = steps[steps.length - 1];
    steps[steps.length - 1] = {
      ...last,
      fieldKeys: [...last.fieldKeys, field.key],
    };
  }
  return { ...prev, fields, steps };
}

export interface ProductJourneyConfigTabProps {
  productId: string | null | undefined;
  productType?: string | null;
  /** Called after a successful save (e.g. switch back to the previous tab). */
  onSaveSuccess?: () => void;
}

export const ProductJourneyConfigTab: React.FC<ProductJourneyConfigTabProps> = ({
  productId,
  productType,
  onSaveSuccess,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [journeyRowId, setJourneyRowId] = React.useState<string | null>(null);
  const [payload, setPayload] =
    React.useState<TProductJourneyConfigPayload>(EMPTY);

  const presets = React.useMemo(
    () => filterPresetsByProductType(productType ?? undefined),
    [productType],
  );

  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ["product-journey-config", productId],
    queryFn: () => getProductJourneyConfigByProductId(productId as string),
    enabled: Boolean(productId),
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    setJourneyRowId(null);
    setPayload(EMPTY);
  }, [productId]);

  React.useEffect(() => {
    if (!productId || !isSuccess) return;
    if (data) {
      setJourneyRowId(data.id);
      setPayload(data.config);
    } else {
      setJourneyRowId(null);
      setPayload(EMPTY);
    }
  }, [productId, data, isSuccess]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!productId) throw new Error("missing_product");
      const normalized = normalizeJourneyPayload(payload);
      const parsed = productJourneyConfigPayloadSchema.safeParse(normalized);
      if (!parsed.success) {
        const msg = parsed.error.issues.map((i) => i.message).join("; ");
        throw new Error(msg || "validation");
      }
      if (journeyRowId) {
        return updateProductJourneyConfig(journeyRowId, parsed.data);
      }
      return createProductJourneyConfig(productId, parsed.data);
    },
    onSuccess: (res) => {
      setJourneyRowId(res.id);
      setPayload(res.config);
      void qc.invalidateQueries({ queryKey: ["product-journey-config", productId] });
      toast({
        title: t("generic.success"),
        description: t("products.journey.saveSuccess"),
      });
      onSaveSuccess?.();
    },
    onError: (e: Error) => {
      toast({
        title: t("generic.error"),
        description: e.message || t("products.journey.saveError"),
        variant: "destructive",
      });
    },
  });

  const addPreset = (presetKey: string) => {
    const def = presets.find((p) => p.presetKey === presetKey);
    if (!def) return;
    if (payload.fields.some((f) => f.key === def.key)) {
      toast({
        title: t("generic.error"),
        description: t("products.journey.duplicateField"),
        variant: "destructive",
      });
      return;
    }
    const label = t(`products.journey.preset.${def.presetKey}.label`);
    const field = presetToField(def, label);
    setPayload((prev) =>
      appendFieldToPayload(prev, field, t("products.journey.defaultStepTitle")),
    );
  };

  const addCustomField = () => {
    let n = 1;
    let key = "custom_field_1";
    while (payload.fields.some((f) => f.key === key)) {
      n += 1;
      key = `custom_field_${n}`;
    }
    const field: TProductJourneyField = {
      key,
      label: t("products.journey.customFieldDefaultLabel"),
      widget: "text",
      fillable: true,
      required: false,
    };
    setPayload((prev) =>
      appendFieldToPayload(prev, field, t("products.journey.defaultStepTitle")),
    );
  };

  const renameFieldKey = (oldKey: string, rawKey: string) => {
    const newKey = rawKey.trim().toLowerCase().replace(/\s+/g, "_");
    if (newKey === oldKey) return;
    if (!SNAKE_CASE_KEY.test(newKey)) {
      toast({
        title: t("generic.error"),
        description: t("products.journey.invalidFieldKey"),
        variant: "destructive",
      });
      return;
    }
    if (payload.fields.some((f) => f.key === newKey)) {
      toast({
        title: t("generic.error"),
        description: t("products.journey.duplicateField"),
        variant: "destructive",
      });
      return;
    }
    setPayload((prev) => ({
      ...prev,
      fields: prev.fields.map((f) =>
        f.key === oldKey ? { ...f, key: newKey } : f,
      ),
      steps: prev.steps.map((s) => ({
        ...s,
        fieldKeys: s.fieldKeys.map((k) => (k === oldKey ? newKey : k)),
      })),
    }));
  };

  const removeField = (key: string) => {
    setPayload((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => f.key !== key),
      steps: prev.steps.map((s) => ({
        ...s,
        fieldKeys: s.fieldKeys.filter((k) => k !== key),
      })),
    }));
  };

  const updateField = (key: string, patch: Partial<TProductJourneyField>) => {
    setPayload((prev) => ({
      ...prev,
      fields: prev.fields.map((f) =>
        f.key === key ? { ...f, ...patch } : f,
      ),
    }));
  };

  const addStep = () => {
    setPayload((prev) => {
      const maxOrder = prev.steps.reduce((m, s) => Math.max(m, s.order), -1);
      return {
        ...prev,
        steps: [
          ...prev.steps,
          {
            id: crypto.randomUUID(),
            order: maxOrder + 1,
            title: "",
            fieldKeys: [],
          },
        ],
      };
    });
  };

  const removeStep = (stepId: string) => {
    setPayload((prev) => {
      const removed = prev.steps.find((s) => s.id === stepId);
      const steps = prev.steps.filter((s) => s.id !== stepId);
      if (!removed || removed.fieldKeys.length === 0) {
        return { ...prev, steps };
      }
      if (steps.length === 0) {
        return {
          ...prev,
          steps: [
            {
              id: crypto.randomUUID(),
              order: 0,
              title: t("products.journey.defaultStepTitle"),
              fieldKeys: [...removed.fieldKeys],
            },
          ],
        };
      }
      return {
        ...prev,
        steps: steps.map((s, i) =>
          i === 0
            ? {
                ...s,
                fieldKeys: [...new Set([...s.fieldKeys, ...removed.fieldKeys])],
              }
            : s,
        ),
      };
    });
  };

  const updateStep = (stepId: string, patch: Partial<TProductJourneyStep>) => {
    setPayload((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)),
    }));
  };

  const moveFieldToStep = (fieldKey: string, targetStepId: string) => {
    setPayload((prev) => {
      const steps = prev.steps.map((s) => ({
        ...s,
        fieldKeys: s.fieldKeys.filter((k) => k !== fieldKey),
      }));
      return {
        ...prev,
        steps: steps.map((s) =>
          s.id === targetStepId
            ? { ...s, fieldKeys: [...s.fieldKeys, fieldKey] }
            : s,
        ),
      };
    });
  };

  if (!productId) {
    return (
      <Card className="p-4">
        <CardHeader>
          <CardTitle>{t("products.journey.tabTitle")}</CardTitle>
          <CardDescription>
            {t("products.journey.needProductSaved")}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-4">
        <CardContent className="py-8 text-muted-foreground text-sm">
          {t("generic.loading")}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="p-4">
        <CardContent className="py-8 text-destructive text-sm">
          {(error as Error)?.message || t("products.journey.loadError")}
        </CardContent>
      </Card>
    );
  }

  const sortedSteps = [...payload.steps].sort((a, b) => a.order - b.order);

  return (
    <Card className="p-4">
      <CardHeader>
        <CardTitle>{t("products.journey.tabTitle")}</CardTitle>
        <CardDescription>{t("products.journey.tabDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="text-sm font-semibold">
              {t("products.journey.presetsTitle")}
            </h3>
            <Button type="button" variant="outline" size="sm" onClick={addCustomField}>
              <Plus className="h-4 w-4 mr-1" />
              {t("products.journey.addCustomField")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <Button
                key={p.presetKey}
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => addPreset(p.presetKey)}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t(`products.journey.preset.${p.presetKey}.label`)}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">
            {t("products.journey.fieldsTitle")}
          </h3>
          {payload.fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("products.journey.noFieldsYet")}
            </p>
          ) : (
            <div className="space-y-4">
              {payload.fields.map((f) => (
                <JourneyFieldEditor
                  key={f.key}
                  field={f}
                  sortedSteps={sortedSteps}
                  onUpdate={(patch) => updateField(f.key, patch)}
                  onRenameKey={(newKey) => renameFieldKey(f.key, newKey)}
                  onMoveToStep={(stepId) => moveFieldToStep(f.key, stepId)}
                  onRemove={() => removeField(f.key)}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">{t("products.journey.stepsTitle")}</h3>
            <Button type="button" variant="outline" size="sm" onClick={addStep}>
              <Plus className="h-4 w-4 mr-1" />
              {t("products.journey.addStep")}
            </Button>
          </div>
          {sortedSteps.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("products.journey.noStepsHint")}
            </p>
          ) : (
            <div className="space-y-3">
              {sortedSteps.map((s) => (
                <div key={s.id} className="border rounded-md p-3 space-y-2">
                  <div className="flex flex-wrap gap-3 items-end justify-between">
                    <div className="flex-1 min-w-[10rem]">
                      <FormLabel className="text-xs">{t("products.journey.stepTitle")}</FormLabel>
                      <Input
                        value={s.title ?? ""}
                        onChange={(e) => updateStep(s.id, { title: e.target.value })}
                        placeholder={t("products.journey.stepTitlePlaceholder")}
                      />
                    </div>
                    <div className="w-24">
                      <FormLabel className="text-xs">{t("products.journey.stepOrder")}</FormLabel>
                      <Input
                        type="number"
                        min={0}
                        value={s.order}
                        onChange={(e) =>
                          updateStep(s.id, {
                            order: Number.parseInt(e.target.value, 10) || 0,
                          })
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive shrink-0"
                      onClick={() => removeStep(s.id)}
                      disabled={sortedSteps.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("products.journey.stepFieldKeys")}:{" "}
                    {s.fieldKeys.length ? s.fieldKeys.join(", ") : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {t("products.journey.saveButton")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
